import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import {
  getScheduledPhasePositiveAmountIssue,
  validateScheduledBudgetWallsWithinUtcFlight,
} from "@common/utils/scheduledBudgetPhaseFormValidation";
import type { FieldErrors } from "react-hook-form";
import type { BudgetData } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedBudgetEditor";
import { wireScheduleToEventWall } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/eventWallTime";
import { getScheduledBudgetPhaseTimelineIssue } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/scheduledBudgetPhaseTimelineValidation";
import * as y from "yup";

/** Passed through `useForm({ context })` for scheduled timeline + flight checks (same pattern as {@link CreateCampaignFlow}). */
export type EditBudgetModalValidationContext = {
  eventTimezone?: string;
  scheduledBudgetFlightBounds?: {
    flightStartIso: string;
    flightEndIso: string;
  } | null;
};

function joinYupPath(base: string | undefined, suffix: string): string {
  if (base == null || base === "") return suffix;
  return `${base}.${suffix}`;
}

/**
 * Yup schema for Ads Engage “Edit budget” / {@link GuidedBudgetEditor} values — mirrors
 * {@link campaignFlowForm} scheduled rules (timeline, positive phase amounts, ad flight window)
 * without campaign `schedule` (flight bounds come from context).
 */
export const EditBudgetModalSchema = y
  .object({
    type: y.string().optional(),
    amount: y
      .object({
        amount: y.mixed().nullable().optional(),
        currency: y.string().optional(),
      })
      .optional(),
    base: y.mixed().optional(),
    segments: y
      .array()
      .of(
        y.object({
          time_start: y.string().optional(),
          time_end: y.string().optional(),
          amount: y
            .object({
              amount: y.mixed().nullable().optional(),
              currency: y.string().optional(),
            })
            .optional(),
        })
      )
      .default([]),
  })
  .test(
    "edit-scheduled-budget",
    "Invalid scheduled budget",
    function (value) {
      if (!value || value.type !== "scheduled") return true;
      const ctx = this.options.context as
        | EditBudgetModalValidationContext
        | undefined;
      const segments = (value as BudgetData).segments ?? [];
      const tz =
        (ctx?.eventTimezone ?? "").trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;

      const fb = ctx?.scheduledBudgetFlightBounds;
      const implicitFlightWalls =
        fb?.flightStartIso?.trim() && fb?.flightEndIso?.trim()
          ? {
              firstPhaseStartWall: wireScheduleToEventWall(
                fb.flightStartIso,
                tz
              ),
              lastPhaseEndWall: wireScheduleToEventWall(fb.flightEndIso, tz),
            }
          : null;

      const timeline = getScheduledBudgetPhaseTimelineIssue(
        segments,
        tz,
        implicitFlightWalls
      );
      if (timeline) {
        return this.createError({
          path: joinYupPath(this.path, timeline.pathSuffix),
          message: timeline.message,
        });
      }

      const amountIssue = getScheduledPhasePositiveAmountIssue(segments);
      if (amountIssue) {
        return this.createError({
          path: joinYupPath(this.path, amountIssue.pathSuffix),
          message: amountIssue.message,
        });
      }

      if (!fb?.flightStartIso?.trim() || !fb?.flightEndIso?.trim()) {
        return true;
      }

      const flightErr = validateScheduledBudgetWallsWithinUtcFlight({
        segments,
        eventTimezone: tz,
        flightStartIso: fb.flightStartIso,
        flightEndIso: fb.flightEndIso,
      });
      if (flightErr) {
        return this.createError({
          path: joinYupPath(this.path, "segments"),
          message: flightErr.message,
        });
      }
      return true;
    }
  );

/** First nested field message for inline alerts (timeline / flight / amount). */
export function flattenEditBudgetModalErrors(
  errors: FieldErrors<BudgetData> | undefined
): string | undefined {
  if (!errors) return undefined;

  const withRoot = errors as FieldErrors<BudgetData> & {
    root?: { message?: string };
  };
  if (typeof withRoot.root?.message === "string" && withRoot.root.message.trim()) {
    return withRoot.root.message.trim();
  }

  const visit = (node: unknown): string | undefined => {
    if (node == null) return undefined;
    if (typeof node !== "object") return undefined;
    const o = node as Record<string, unknown>;
    if (typeof o.message === "string" && o.message.trim()) return o.message;
    if (Array.isArray(o)) {
      for (const item of o) {
        const m = visit(item);
        if (m) return m;
      }
      return undefined;
    }
    for (const [key, v] of Object.entries(o)) {
      if (key === "ref" || key === "types") continue;
      const m = visit(v);
      if (m) return m;
    }
    return undefined;
  };
  return visit(errors);
}

type RowFieldWithMessage = { message?: string };

/**
 * Per-phase validation copy for the Edit budget modal (one string per row: time + amount messages
 * concatenated when multiple fields fail on the same row).
 */
export function getScheduledBudgetSegmentRowErrorMessages(
  errors: FieldErrors<BudgetData> | undefined
): Map<number, string> | undefined {
  const rows = errors?.segments;
  if (!Array.isArray(rows)) return undefined;
  const out = new Map<number, string>();
  rows.forEach((row, i) => {
    if (!row || typeof row !== "object") return;
    const r = row as {
      time_start?: RowFieldWithMessage;
      time_end?: RowFieldWithMessage;
      amount?: { amount?: RowFieldWithMessage };
    };
    const parts: string[] = [];
    const ts = r.time_start?.message?.trim();
    const te = r.time_end?.message?.trim();
    const am = r.amount?.amount?.message?.trim();
    if (ts) parts.push(ts);
    if (te) parts.push(te);
    if (am) parts.push(am);
    if (parts.length) out.set(i, parts.join(" "));
  });
  return out.size ? out : undefined;
}

/**
 * First segment field that failed validation — used to outline the offending control in the
 * phased budget editor.
 */
export function getFirstScheduledBudgetSegmentFieldOutline(
  errors: FieldErrors<BudgetData> | undefined
):
  | { kind: "time_start" | "time_end"; segmentIndex: number }
  | { kind: "amount"; segmentIndex: number }
  | null {
  const rows = errors?.segments;
  if (!Array.isArray(rows)) return null;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i] as
      | {
          time_start?: RowFieldWithMessage;
          time_end?: RowFieldWithMessage;
          amount?: { amount?: RowFieldWithMessage };
        }
      | undefined;
    if (!row) continue;
    if (row.time_start?.message?.trim()) {
      return { kind: "time_start", segmentIndex: i };
    }
    if (row.time_end?.message?.trim()) {
      return { kind: "time_end", segmentIndex: i };
    }
    if (row.amount?.amount?.message?.trim()) {
      return { kind: "amount", segmentIndex: i };
    }
  }
  return null;
}
